import { pipe, Observable, MonoTypeOperatorFunction, merge, ReplaySubject, timer, throwError } from 'rxjs';
import { map, catchError, first, skip, timeout, take, delayWhen } from 'rxjs/operators';
import { TranslatableError, ErrorType, HandledError } from '@cordic/request';
/**
 * Used in rxjs pipe to map output to void and handle errors
 *
 * In the registration pages response data is not required to be
 * handled by the calling component but by this service and emitted
 * through other services or observables.
 *
 * The components behaviour therefore is to assume an empty
 * response is successful, any returned params are a handled error
 * and any thrown params are an unhandled error.
 */
export function mapToVoid<T>()
{
  return pipe(
    map<T, void>(() => {
        return;
    }), catchError(async (err) => handleError(err)));
}

/**
 * Used in rxjs catchError block to map an error
 *
 * Expected and unexpected errors are handled through different
 * mechanisms in the UI.
 * This method will return an error as an observable string
 * if an error code was returned via the server.
 *
 * Otherwise it will throw an error as an object with a translating string
 * going into the appropriate catch blocks, this applies to
 * http statuses other than 200.
 *
 * @param err An error
 */
function handleError(err: TranslatableError)
{
  if (err.errorType === ErrorType.HANDLED)
  {
    return (err as HandledError).translatableMessage?.pipe(
      map(translatableMessage => ({
          ...err,
          toString: () => translatableMessage
        })
      )
    );
  }
  throw err;
}

export function withFirstFrom<T>(observableInput: Observable<T>): Promise<T | undefined>
{
  return observableInput.pipe(first()).toPromise();
}

export function timeoutFirst<T>(due: number): MonoTypeOperatorFunction<T>
{
  return (source) => {
      return merge(
        source.pipe(timeout(due), take(1)),
        source.pipe(skip(1))
      );
    };
}

export function eagerlyObserve<T>(project: Observable<T>, afterListener?: () => any) {
  return new Observable<T>(subscriber =>
    {
      const subject = new ReplaySubject<T>(1);
      const subscription = project.pipe(first()).subscribe(subject);
      subscription.add(subject.pipe(first()).subscribe(subscriber));
      if (afterListener)
      {
        afterListener();
      }
      return subscription;
    });
}

export function defaultRetryStrategy(infinite = false, ...delays: number[])
{
  if (delays.length === 0)
  {
    delays = [1000, 2500, 3000];
  }

  return (attempts: Observable<any>) => {
    return attempts.pipe(
      delayWhen((err, i) =>
      {
        if (i < delays.length)
        {
          return timer(delays[i]);
        }
        if (infinite)
        {
          return timer(delays[delays.length - 1]);
        }
        return throwError(err) as Observable<any>;
      })
    );
  };
}
